import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { Truck, Package, CheckCircle } from "@phosphor-icons/react";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";

/**
 * PendingReceiptCard — surfaces the doctor's most-urgent pre-treatment
 * action: confirm that a shipment has arrived at the clinic. Lives on
 * the Workflow tab when a shipment is in_transit / pending_dispatch.
 *
 * Why this exists separately from the per-shipment row inside
 * LabProductionTab: the Workflow tab is the actions hub. The doctor
 * shouldn't have to bounce to the Production tab + find the right
 * shipment row to acknowledge receipt. One prominent card here closes
 * that loop in-place.
 *
 * Auto-hides when there's nothing pending — the wrapper checks first.
 */
export default function PendingReceiptCard({ caseId, onConfirmed }) {
    const [shipment, setShipment] = useState(null);
    const [loading, setLoading] = useState(true);
    const [dialogOpen, setDialogOpen] = useState(false);

    const reload = async () => {
        try {
            const r = await api.get(`/cases/${caseId}/shipments`);
            const pending = (r.data || []).find((s) => s.status !== "received");
            setShipment(pending || null);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        if (caseId) reload();
    }, [caseId]);  // eslint-disable-line react-hooks/exhaustive-deps

    if (loading || !shipment) return null;

    const inTransit = shipment.status === "in_transit";
    return (
        <div
            className="rounded-2xl border-2 border-amber-300 bg-amber-50 p-5 shadow-sm"
            data-testid="workflow-pending-receipt-card"
        >
            <div className="flex items-start gap-4 flex-wrap">
                <div className="w-12 h-12 rounded-full bg-amber-100 grid place-items-center shrink-0">
                    {inTransit ? (
                        <Truck size={22} weight="fill" className="text-amber-700" />
                    ) : (
                        <Package size={22} weight="fill" className="text-amber-700" />
                    )}
                </div>
                <div className="flex-1 min-w-[200px]">
                    <div className="text-[10px] font-bold uppercase tracking-wider text-amber-700">
                        {inTransit ? "Shipment in transit" : "Shipment being prepared"}
                    </div>
                    <div className="font-display text-lg font-semibold leading-tight mt-1">
                        {shipment.kind === "retainers"
                            ? "Retainers"
                            : `Phase ${shipment.phase_number}`}
                        {" · "}
                        Stages {shipment.stage_from}–{shipment.stage_to}
                    </div>
                    {(shipment.courier || shipment.tracking_number) && (
                        <div className="text-sm text-amber-900 mt-1 font-mono">
                            {shipment.courier ? `${shipment.courier} ` : ""}
                            {shipment.tracking_number ? `#${shipment.tracking_number}` : ""}
                        </div>
                    )}
                    {inTransit ? (
                        <p className="text-sm mt-2 leading-relaxed text-amber-900/90">
                            When the package arrives at your clinic, tap{" "}
                            <strong>Confirm receipt</strong> so the patient's case advances.
                        </p>
                    ) : (
                        <p className="text-sm mt-2 leading-relaxed text-amber-900/90">
                            Your production team is preparing this shipment. We'll move it to
                            "in transit" the moment the courier picks it up.
                        </p>
                    )}
                </div>
                {inTransit && (
                    <Button
                        onClick={() => setDialogOpen(true)}
                        className="rounded-full h-11 px-6 shrink-0 bg-amber-600 hover:bg-amber-700"
                        data-testid="workflow-confirm-receipt-btn"
                    >
                        <CheckCircle size={15} weight="fill" className="mr-1.5" />
                        Confirm receipt
                    </Button>
                )}
            </div>

            <ConfirmReceiptDialog
                open={dialogOpen}
                onClose={() => setDialogOpen(false)}
                caseId={caseId}
                shipment={shipment}
                onConfirmed={() => { setShipment(null); onConfirmed?.(); }}
            />
        </div>
    );
}


/* ─── confirm receipt dialog — same shape as ShipmentsList's dialog ───── */

function ConfirmReceiptDialog({ open, onClose, caseId, shipment, onConfirmed }) {
    const [receivedAt, setReceivedAt] = useState(() => todayIsoDate());
    const [notes, setNotes] = useState("");
    const [saving, setSaving] = useState(false);

    const submit = async () => {
        try {
            setSaving(true);
            const iso = receivedAt ? new Date(`${receivedAt}T12:00:00`).toISOString() : undefined;
            await api.post(
                `/cases/${caseId}/shipments/${shipment.id}/confirm-receipt`,
                { received_at: iso, notes: notes.trim() },
            );
            toast.success("Receipt confirmed.");
            onConfirmed?.();
            onClose();
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Couldn't confirm receipt. Try again.");
        } finally {
            setSaving(false);
        }
    };

    return (
        <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
            <DialogContent className="max-w-md" data-testid="workflow-confirm-receipt-dialog">
                <DialogHeader>
                    <DialogTitle className="font-display flex items-center gap-2">
                        <Package size={18} weight="duotone" className="text-emerald-600" />
                        Confirm aligner receipt
                    </DialogTitle>
                </DialogHeader>
                <div className="space-y-4">
                    <div className="text-sm text-muted-foreground">
                        Confirm you've received <span className="font-semibold text-foreground">
                            {shipment.kind === "retainers"
                                ? "the retainers"
                                : `Phase ${shipment.phase_number} (stages ${shipment.stage_from}–${shipment.stage_to})`}
                        </span> at your clinic.
                    </div>
                    <div>
                        <label className="text-xs font-bold uppercase tracking-wider text-muted-foreground mb-1.5 block">
                            Date received
                        </label>
                        <Input
                            type="date"
                            value={receivedAt}
                            onChange={(e) => setReceivedAt(e.target.value)}
                            max={todayIsoDate()}
                            data-testid="workflow-confirm-date-input"
                        />
                    </div>
                    <div>
                        <label className="text-xs font-bold uppercase tracking-wider text-muted-foreground mb-1.5 block">
                            Notes (optional)
                        </label>
                        <Textarea
                            value={notes}
                            onChange={(e) => setNotes(e.target.value)}
                            placeholder="e.g. Package intact, all stages accounted for."
                            rows={2}
                            maxLength={500}
                            data-testid="workflow-confirm-notes-input"
                        />
                    </div>
                    <div className="flex items-center justify-end gap-2 pt-2 border-t border-border">
                        <Button variant="ghost" onClick={onClose}>Cancel</Button>
                        <Button
                            onClick={submit}
                            disabled={saving || !receivedAt}
                            className="bg-emerald-600 hover:bg-emerald-700 text-white"
                            data-testid="workflow-confirm-submit-btn"
                        >
                            <CheckCircle size={14} className="mr-1.5" weight="fill" />
                            {saving ? "Saving…" : "Confirm receipt"}
                        </Button>
                    </div>
                </div>
            </DialogContent>
        </Dialog>
    );
}

function todayIsoDate() {
    const d = new Date();
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
}
